import { skipToken } from "@reduxjs/toolkit/query";
import {
  computedFieldResourceTypeLabels,
  type ComputedFieldDefinition,
  type ComputedFieldDefinitionId,
} from "@repo/app-types/entities/computedField";
import {
  CustomFieldResourceType,
  customFieldResourceTypeLabels,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Size, toHumanReadableString } from "@repo/ui/helpers/helpers";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { PageWidth } from "@repo/ui/Navigation/pageWidth";
import type { SimpleSelectOption } from "@repo/ui/Select/simpleSelect";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { TableSkeleton } from "@repo/ui/Skeletons/skeletons";
import { SmallTimeStamp } from "@repo/ui/SmallTimeStamp/smallTimeStamp";
import {
  IconBolt,
  IconChevronDown,
  IconFilter,
  IconMenu3,
  IconPencil,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState, type FC } from "react";
import { MenuTrigger } from "react-aria-components";
import {
  useAllComputedFieldDefinitionsQuery,
  useDeleteComputedFieldDefinitionMutation,
} from "../../../api/entities/computedField.js";
import {
  useAllUserCustomFieldDefinitionsQuery,
  useDeleteUserCustomFieldDefinitionMutation,
} from "../../../api/entities/userCustomField.js";
import { PageBody, PageLayout } from "../../../components/layout/pageLayout.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";
import { settingsSections } from "../settingsSections.js";
import { SettingsTable } from "../shared/settingsTable.js";
import { SettingsTableBodySection } from "../shared/settingsTableBodySection.js";
import { SettingsTableDataCell } from "../shared/settingsTableDataCell.js";
import { SettingsTableEmptyBodyContent } from "../shared/settingsTableEmptyBodyContent.js";
import { SettingsTableHeaderCell } from "../shared/settingsTableHeaderCell.js";
import { SettingsTableHeaderSection } from "../shared/settingsTableHeaderSection.js";
import { ComputedFieldDefinitionCreateDialog } from "./computedFieldDefinitionCreateDialog.js";
import { ComputedFieldDefinitionDeleteDialog } from "./computedFieldDefinitionDeleteDialog.js";
import { ComputedFieldDefinitionEditDialog } from "./computedFieldDefinitionEditDialog.js";
import { UserCustomFieldDefinitionCreateDialog } from "./userCustomFieldDefinitionCreateDialog.js";
import { UserCustomFieldDefinitionDeleteDialog } from "./userCustomFieldDefinitionDeleteDialog.js";
import { useShouldShowCustomerInteractionResourceType } from "./useShouldShowCustomerInteractionResourceType.js";
import { useShouldShowInterviewResourceType } from "./useShouldShowInterviewResourceType.js";

type ResourceTypeFilterValue =
  | null
  | typeof CustomFieldResourceType.CONTACT
  | typeof CustomFieldResourceType.INTERVIEW
  | typeof CustomFieldResourceType.GRADING;

const allResourcesFilterOption: SimpleSelectOption<ResourceTypeFilterValue> = {
  label: "All resources",
  value: null,
};

const contactFilterOption: SimpleSelectOption<ResourceTypeFilterValue> = {
  label: customFieldResourceTypeLabels[CustomFieldResourceType.CONTACT],
  value: CustomFieldResourceType.CONTACT,
};

const interviewFilterOption: SimpleSelectOption<ResourceTypeFilterValue> = {
  label: customFieldResourceTypeLabels[CustomFieldResourceType.INTERVIEW],
  value: CustomFieldResourceType.INTERVIEW,
};

const customerInteractionFilterOption: SimpleSelectOption<ResourceTypeFilterValue> =
  {
    label: customFieldResourceTypeLabels[CustomFieldResourceType.GRADING],
    value: CustomFieldResourceType.GRADING,
  };

type CustomFieldRow = {
  kind: "custom";
  id: string;
  resource_type: CustomFieldResourceType;
  display_name: string;
  field_name: string;
  field_type: string;
  created_at: string;
  raw: CustomFieldDefinition;
};

type ComputedFieldRow = {
  kind: "computed";
  id: ComputedFieldDefinitionId;
  resource_type: CustomFieldResourceType;
  display_name: string;
  created_at: string;
  raw: ComputedFieldDefinition;
};

type MixedFieldRow = CustomFieldRow | ComputedFieldRow;

export const UserCustomFieldSettingsSection: FC = () => {
  const [selectedResourceType, setSelectedResourceType] =
    useState<ResourceTypeFilterValue>(null);

  const isComputedFieldsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.TeamPerformanceTab,
  );

  const showInterviewResourceType = useShouldShowInterviewResourceType();
  const showCustomerInteractionResourceType =
    useShouldShowCustomerInteractionResourceType();

  const resourceTypeFilterOptions = useMemo<
    readonly SimpleSelectOption<ResourceTypeFilterValue>[]
  >(() => {
    const options: SimpleSelectOption<ResourceTypeFilterValue>[] = [
      allResourcesFilterOption,
      contactFilterOption,
    ];
    if (showInterviewResourceType) {
      options.push(interviewFilterOption);
    }
    if (showCustomerInteractionResourceType) {
      options.push(customerInteractionFilterOption);
    }
    return options;
  }, [showInterviewResourceType, showCustomerInteractionResourceType]);

  const resourceNounPhrase = useMemo(() => {
    const nouns = ["contacts"];
    if (showInterviewResourceType) {
      nouns.push("interviews");
    }
    if (showCustomerInteractionResourceType) {
      nouns.push("customer interactions");
    }
    return new Intl.ListFormat("en", {
      style: "long",
      type: "conjunction",
    }).format(nouns);
  }, [showInterviewResourceType, showCustomerInteractionResourceType]);

  const { data: customFields, isLoading: isLoadingCustom } =
    useAllUserCustomFieldDefinitionsQuery();
  const { data: computedFields, isLoading: isLoadingComputed } =
    useAllComputedFieldDefinitionsQuery(
      isComputedFieldsEnabled ? undefined : skipToken,
    );

  const [deleteUserCustomField] = useDeleteUserCustomFieldDefinitionMutation();
  const [deleteComputedField] = useDeleteComputedFieldDefinitionMutation();

  const [customFieldIdToDelete, setCustomFieldIdToDelete] = useState<
    string | undefined
  >(undefined);
  const [computedFieldIdToDelete, setComputedFieldIdToDelete] = useState<
    ComputedFieldDefinitionId | undefined
  >(undefined);
  const [computedFieldToEdit, setComputedFieldToEdit] = useState<
    ComputedFieldDefinition | undefined
  >(undefined);
  const [isCreateCustomFieldDialogOpen, setIsCreateCustomFieldDialogOpen] =
    useState(false);
  const [isCreateComputedFieldDialogOpen, setIsCreateComputedFieldDialogOpen] =
    useState(false);

  const rows = useMemo<readonly MixedFieldRow[]>(() => {
    const customRows: CustomFieldRow[] = (customFields ?? []).map((field) => ({
      kind: "custom",
      id: field.id,
      resource_type: field.resource_type,
      display_name: field.display_name,
      field_name: field.field_name,
      field_type: field.field_type,
      created_at: field.created_at,
      raw: field,
    }));

    const computedRows: ComputedFieldRow[] = (computedFields ?? []).map(
      (field) => ({
        kind: "computed",
        id: field.id,
        resource_type: field.resource_type,
        display_name: field.display_name,
        created_at: field.created_at,
        raw: field,
      }),
    );

    const allRows: MixedFieldRow[] = [...customRows, ...computedRows];

    if (selectedResourceType !== null) {
      return allRows.filter(
        (row) => row.resource_type === selectedResourceType,
      );
    }
    return allRows;
  }, [customFields, computedFields, selectedResourceType]);

  const isLoading = isLoadingCustom || isLoadingComputed;
  const totalCount =
    (customFields?.length ?? 0) + (computedFields?.length ?? 0);

  return (
    <PageLayout>
      <PageHeader
        width={PageWidth.Standard}
        title={settingsSections.customFields.label}
        actions={
          <Can
            I={PermissionActions.CREATE}
            a={PermissionSubjects.USER_CUSTOM_FIELDS}
            passThrough={true}
          >
            {(allowed) =>
              isComputedFieldsEnabled ? (
                <div className="flex items-stretch">
                  <Button
                    size={Size.SMALL}
                    variant={ButtonVariant.Primary}
                    icon={<IconPlus className="size-4" />}
                    onClick={() => setIsCreateCustomFieldDialogOpen(true)}
                    isDisabled={!allowed}
                    className="rounded-r-none"
                  >
                    Create field
                  </Button>
                  <MenuTrigger>
                    <Button
                      size={Size.SMALL}
                      variant={ButtonVariant.Primary}
                      icon={<IconChevronDown className="size-4" />}
                      isDisabled={!allowed}
                      className="-ml-px rounded-l-none border-l border-black/15 px-1.5"
                    />
                    <SimpleMenuPopover placement="bottom end">
                      <SimpleMenu aria-label="Create field options">
                        <SimpleMenuItem
                          onAction={() =>
                            setIsCreateCustomFieldDialogOpen(true)
                          }
                        >
                          <div className="flex items-center gap-2">
                            <IconMenu3 className="size-4 shrink-0" />
                            <span>Custom field</span>
                          </div>
                        </SimpleMenuItem>
                        <SimpleMenuItem
                          onAction={() =>
                            setIsCreateComputedFieldDialogOpen(true)
                          }
                        >
                          <div className="flex items-center gap-2">
                            <IconBolt className="size-4 shrink-0" />
                            <span>Computed field</span>
                          </div>
                        </SimpleMenuItem>
                      </SimpleMenu>
                    </SimpleMenuPopover>
                  </MenuTrigger>
                </div>
              ) : (
                <Button
                  size={Size.SMALL}
                  variant={ButtonVariant.Primary}
                  icon={<IconPlus className="size-4" />}
                  onClick={() => setIsCreateCustomFieldDialogOpen(true)}
                  isDisabled={!allowed}
                >
                  Create field
                </Button>
              )
            }
          </Can>
        }
      />
      <UserCustomFieldDefinitionDeleteDialog
        customFieldDefinitionId={customFieldIdToDelete}
        onConfirm={deleteUserCustomField}
        onRequestClose={() => setCustomFieldIdToDelete(undefined)}
      />
      <ComputedFieldDefinitionDeleteDialog
        computedFieldDefinitionId={computedFieldIdToDelete}
        onConfirm={deleteComputedField}
        onRequestClose={() => setComputedFieldIdToDelete(undefined)}
      />
      <ComputedFieldDefinitionEditDialog
        computedField={computedFieldToEdit}
        onClose={() => setComputedFieldToEdit(undefined)}
      />

      <PageBody width={PageWidth.Standard}>
        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            Define custom fields to store additional information about{" "}
            {resourceNounPhrase}. These fields can be used via the Alpharun API,
            the CSV importer, message templates, and more.
            {isComputedFieldsEnabled
              ? " Computed fields derive new metrics from existing custom fields and can be added as columns in team performance views."
              : null}
          </div>

          <div className="flex w-64 items-center space-x-2">
            <SimpleSelect<ResourceTypeFilterValue>
              size={Size.SMALL}
              leftAccessory={<IconFilter className="h-4 w-4" />}
              options={resourceTypeFilterOptions}
              value={selectedResourceType}
              onChange={setSelectedResourceType}
              placeholder="Filter by resource"
            />
          </div>

          <SettingsTable>
            <SettingsTableHeaderSection>
              <tr>
                <SettingsTableHeaderCell className="w-[15%]">
                  Resource
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[30%]">
                  Field name
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[25%]">
                  Field key
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[15%]">
                  Type
                </SettingsTableHeaderCell>
                <SettingsTableHeaderCell className="w-[15%]">
                  Created
                </SettingsTableHeaderCell>
              </tr>
            </SettingsTableHeaderSection>
            <SettingsTableBodySection>
              {isLoading ? <TableSkeleton /> : null}

              {rows.length === 0 ? (
                <SettingsTableEmptyBodyContent colSpan={6}>
                  {totalCount === 0
                    ? "No custom fields have been created yet."
                    : "No custom fields match the selected filter."}
                </SettingsTableEmptyBodyContent>
              ) : null}

              {rows.map((row) => (
                <tr key={`${row.kind}:${row.id}`} className="group relative">
                  <SettingsTableDataCell>
                    <span className="text-sm text-muted-foreground">
                      {row.kind === "custom"
                        ? customFieldResourceTypeLabels[row.resource_type]
                        : computedFieldResourceTypeLabels[
                            row.raw.resource_type
                          ]}
                    </span>
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    <span className="font-semibold break-words whitespace-normal">
                      {row.display_name}
                    </span>
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    {row.kind === "custom" ? (
                      <span className="font-mono text-xs text-muted-foreground">
                        {row.field_name}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    {row.kind === "custom" ? (
                      <span className="text-sm text-muted-foreground">
                        {toHumanReadableString(row.field_type)}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
                        Number
                        <IconBolt
                          className="size-4 text-gray-400"
                          aria-label="Computed field"
                        />
                      </span>
                    )}
                  </SettingsTableDataCell>
                  <SettingsTableDataCell>
                    <SmallTimeStamp
                      timestamp={row.created_at}
                      className="text-sm text-muted-foreground"
                    />

                    <div className="absolute top-1/2 right-3 hidden -translate-y-1/2 cursor-pointer items-center space-x-2 text-muted-foreground group-hover:flex">
                      {row.kind === "computed" ? (
                        <Can
                          I={PermissionActions.UPDATE}
                          a={PermissionSubjects.USER_CUSTOM_FIELDS}
                        >
                          <IconButton
                            variant={ButtonVariant.Secondary}
                            icon={<IconPencil className="h-4 w-4" />}
                            onClick={() => setComputedFieldToEdit(row.raw)}
                          />
                        </Can>
                      ) : null}
                      <Can
                        I={PermissionActions.DELETE}
                        a={PermissionSubjects.USER_CUSTOM_FIELDS}
                      >
                        <IconButton
                          variant={ButtonVariant.Secondary}
                          icon={<IconTrash className="h-4 w-4" />}
                          onClick={() => {
                            if (row.kind === "custom") {
                              setCustomFieldIdToDelete(row.id);
                            } else {
                              setComputedFieldIdToDelete(row.id);
                            }
                          }}
                        />
                      </Can>
                    </div>
                  </SettingsTableDataCell>
                </tr>
              ))}
            </SettingsTableBodySection>
          </SettingsTable>
        </div>
      </PageBody>

      <UserCustomFieldDefinitionCreateDialog
        isOpen={isCreateCustomFieldDialogOpen}
        onClose={() => setIsCreateCustomFieldDialogOpen(false)}
      />
      <ComputedFieldDefinitionCreateDialog
        isOpen={isCreateComputedFieldDialogOpen}
        onClose={() => setIsCreateComputedFieldDialogOpen(false)}
      />
    </PageLayout>
  );
};
